import React, { MouseEventHandler, useMemo } from 'react';
import classNames from 'classnames';
import { Box, Typography } from '@mui/material';
import { mapDegreesCardinal } from '@surfline/web-common';
import { AutoplayOptions, PlayerSkins } from 'components/CamPlayerV2/constants';
import CamIcon from 'components/Icons/CamIcon';
import CamPlayerV2 from 'components/CamPlayerV2';
import CamStill from 'components/CamStill';
import { CameraInsightsIcon } from 'components/Icons/CameraInsightsIcon';
import ConditionObservation from 'components/ConditionObservation';
import CurrentWaveRating from 'components/CurrentWaveRating';
import ObservedBadge from 'components/ObservedBadge';
import SpotSatelliteWithPin from 'components/SpotSatelliteWithPin';
import WavetrakLink from 'components/WavetrakLink';
import conditionIsFlat from 'utils/conditionIsFlat';
import { isSurfObserved, isWindObserved } from 'utils/conditionObservations';
import { roundSurfHeight } from 'utils/roundUnits';
import { useUserPermissionStatus } from 'selectors/user';
import { SL_WEB_CAM_PAYWALL_REDESIGN } from 'common/treatments';
import formatWaveHeight from 'utils/formatWaveHeight';
import { useTreatments } from 'utils/treatments';
import type { Camera } from 'types/camera';
import type { ConditionObservers } from 'types/observations';
import type { ObservationType } from 'types/surf';
import type { SpotReportData } from 'types/spot';
import type { Units } from 'types/units';

import styles from './SpotPreviewCardWithClarity.module.scss';

type ObservedSurf = {
  by: ConditionObservers;
  iconUrl?: string;
  at: number | null;
};

type ObservedWind = {
  value: string;
  by: ConditionObservers;
  at: number | null;
};

interface Props {
  cardIndex: number;
  clipPathIds: {
    surfBarGraphIcon: string;
    windBarGraphIcon: string;
  };
  href?: string;
  layout?: 'COMPACT' | 'THUMBNAIL' | 'FULL';
  onClickHandler?: MouseEventHandler;
  showFullscreenControl?: boolean;
  spotReport: SpotReportData;
  stopStream?: boolean;
  units: Units;
}

const SpotPreviewCardWithClarity: React.FC<Props> = ({
  cardIndex,
  clipPathIds,
  href,
  layout = 'FULL',
  onClickHandler,
  showFullscreenControl = true,
  spotReport,
  stopStream = false,
  units,
}) => {
  const { hasAdFreeCamPermissions, hasAdSupportedCamPermissions } = useUserPermissionStatus();
  const treatments = useTreatments();
  const camPaywallRedesignTreatment = treatments[SL_WEB_CAM_PAYWALL_REDESIGN];
  const defaultTimezone = spotReport?.timezone || 'America/Los_Angeles';

  const showSingleCamIcon = useMemo(
    () => spotReport?.cameras.length === 1 && !spotReport?.insightsCameraId,
    [spotReport?.cameras, spotReport?.insightsCameraId],
  );

  const showMultiCamIcon = useMemo(
    () => spotReport?.cameras.length > 1 && !spotReport?.insightsCameraId,
    [spotReport?.cameras, spotReport?.insightsCameraId],
  );

  const showCameraInsightsIcon = !!spotReport?.insightsCameraId;

  const isFlat = useMemo(
    () => conditionIsFlat(spotReport?.waveHeight?.max, units?.waveHeight),
    [spotReport?.waveHeight, units],
  );

  const observedSurfValue = useMemo(() => {
    if (isFlat) return 'Flat';
    return `${roundSurfHeight(
      spotReport?.waveHeight?.min,
      units?.waveHeight || 'ft',
    )}-${roundSurfHeight(spotReport?.waveHeight?.max, units?.waveHeight || 'ft')}`;
  }, [spotReport?.waveHeight, isFlat, units]);

  const observedSurf = useMemo<ObservedSurf>(() => {
    let observedSurfObj: ObservedSurf = {
      by: 'LOTUS',
      iconUrl: undefined,
      at: null,
    };

    if (
      spotReport?.waveHeight?.type &&
      (spotReport?.waveHeight?.type as ObservationType) === 'CAMERA'
    ) {
      observedSurfObj = { ...observedSurfObj, by: 'Smart Cam' };
    }

    if (
      spotReport?.waveHeight?.type &&
      (spotReport?.waveHeight?.type as ObservationType) === 'HUMAN'
    ) {
      observedSurfObj = { ...observedSurfObj, by: 'Forecaster' };
    }

    if (typeof spotReport?.waveHeight?.forecaster === 'object') {
      if (spotReport?.waveHeight?.forecaster?.iconUrl) {
        observedSurfObj = {
          ...observedSurfObj,
          iconUrl: spotReport?.waveHeight?.forecaster.iconUrl as string,
        };
      }
    }

    if (spotReport?.waveHeight?.lastObserved) {
      observedSurfObj = { ...observedSurfObj, at: spotReport?.waveHeight?.lastObserved };
    }

    return observedSurfObj;
  }, [
    spotReport?.waveHeight?.forecaster,
    spotReport?.waveHeight?.lastObserved,
    spotReport?.waveHeight?.type,
  ]);

  const formattedWaveHeight = useMemo(
    () => formatWaveHeight(units?.waveHeight, true),
    [units?.waveHeight],
  );

  const observedWind = useMemo<ObservedWind>(() => {
    let observedWindObj: ObservedWind = {
      value: '0',
      by: 'GFS',
      at: null,
    };

    if (spotReport?.wind?.speed) {
      observedWindObj = { ...observedWindObj, value: spotReport?.wind?.speed.toString() };
    }

    if (spotReport?.wind?.lastObserved) {
      observedWindObj = { ...observedWindObj, by: 'Wind Station' };
    }

    if (spotReport?.wind?.lastObserved) {
      observedWindObj = { ...observedWindObj, at: spotReport?.wind?.lastObserved };
    }

    return observedWindObj;
  }, [spotReport?.wind]);

  const showSurfObservedBadge = useMemo(
    () => isSurfObserved(spotReport?.waveHeight),
    [spotReport?.waveHeight],
  );

  const showWindObservedBadge = useMemo(() => isWindObserved(spotReport?.wind), [spotReport?.wind]);

  const windCardinalDirection = useMemo(
    () => mapDegreesCardinal(spotReport?.wind?.direction),
    [spotReport?.wind?.direction],
  );

  const activeCameras = useMemo(
    () => spotReport?.cameras.filter((cam: Camera) => !cam.status.isDown) || [],
    [spotReport?.cameras],
  );

  const geo = useMemo(
    () => ({
      lat: spotReport?.lat,
      lon: spotReport?.lon,
    }),
    [spotReport?.lat, spotReport?.lon],
  );

  const playerProps = useMemo(
    () => ({
      autoplay: AutoplayOptions.AUTOPLAY_ENABLED,
      camera: activeCameras[0] as Camera,
      dontTrack: true,
      isFavorite: true,
      isPermitted: hasAdFreeCamPermissions,
      latestRewind: false,
      location: 'Homepage Spot Preview Card',
      pauseWhenNotVisible: true,
      playerId: `${spotReport._id}-${cardIndex}`,
      playerSkin: PlayerSkins.THUMBNAIL,
      showFullscreenControl,
      showOverlays: true,
      spotId: spotReport._id,
      spotName: spotReport.name,
      stopStream,
      segmentProperties: {
        location: 'Favorites Carousel',
        pageName: 'Home',
        spotId: spotReport._id,
        spotName: spotReport.name,
      },
    }),
    [
      activeCameras,
      hasAdFreeCamPermissions,
      cardIndex,
      showFullscreenControl,
      stopStream,
      spotReport,
    ],
  );

  const camPlayerComponent = useMemo(() => {
    if (layout === 'FULL') {
      const isFreeUserInPaywallSplit =
        !hasAdFreeCamPermissions &&
        !hasAdSupportedCamPermissions &&
        camPaywallRedesignTreatment === 'on';

      if (activeCameras.length === 0 || isFreeUserInPaywallSplit) {
        return (
          <Box
            className={styles.imageContainer}
            data-testid="spot-preview-card-with-clarity-satellite-with-pin"
          >
            <SpotSatelliteWithPin spotName={spotReport.name} geo={geo} size="large" />
          </Box>
        );
      }

      // Guard on streamUrl — recommendation spots may have cameras without a stream URL
      if (hasAdFreeCamPermissions && !stopStream && activeCameras[0]?.streamUrl) {
        return (
          <Box className={styles.imageContainer} data-testid={`cam-live-${activeCameras[0]._id}`}>
            <CamPlayerV2 {...playerProps} />
          </Box>
        );
      }

      return (
        <Box className={styles.imageContainer} data-testid={`cam-still-${activeCameras[0]._id}`}>
          <CamStill imageUrl={activeCameras[0].stillUrlFull} />
        </Box>
      );
    }
    return null;
  }, [
    camPaywallRedesignTreatment,
    geo,
    hasAdFreeCamPermissions,
    hasAdSupportedCamPermissions,
    activeCameras,
    layout,
    playerProps,
    spotReport.name,
    stopStream,
  ]);

  const cardContent = useMemo(
    () => (
      <>
        {layout === 'COMPACT' && (
          <>
            {spotReport.conditions.value && (
              <CurrentWaveRating direction="VERTICAL" rating={spotReport.conditions.value} />
            )}
            <Box className={styles.data}>
              <Box className={styles.spotMeta}>
                <Typography
                  variant="subHeadline"
                  component="p"
                  className={styles.spotName}
                  data-testid="spot-preview-card-spot-name"
                >
                  {spotReport.name}
                </Typography>
                {showSingleCamIcon && <CamIcon />}
                {showMultiCamIcon && <CamIcon isMultiCam />}
                {showCameraInsightsIcon && <CameraInsightsIcon />}
              </Box>
              <Box className={styles.conditions}>
                <Box className={styles.surfHeight} data-testid="spot-preview-card-surf-height">
                  <Typography component="p" className={styles.surfHeightValue}>
                    <Typography
                      variant="title3"
                      component="span"
                      data-testid="spot-preview-card-surf-height-value"
                    >
                      {spotReport.waveHeight.min}-{spotReport.waveHeight.max}
                    </Typography>
                    <Typography
                      variant="callout1"
                      component="span"
                      data-testid="spot-preview-card-surf-height-unit"
                    >
                      {formattedWaveHeight}
                    </Typography>
                  </Typography>
                  {showSurfObservedBadge && (
                    <Box
                      className={styles.observedBadge}
                      data-testid="spot-preview-card-surf-observed-badge"
                    >
                      <ObservedBadge />
                    </Box>
                  )}
                </Box>
                <Box className={styles.wind} data-testid="spot-preview-card-wind">
                  <Typography component="p" className={styles.windValue}>
                    <Typography
                      variant="title3"
                      component="span"
                      data-testid="spot-preview-card-wind-value"
                    >
                      {spotReport.wind.speed}
                    </Typography>
                    <Typography
                      variant="callout1"
                      component="span"
                      data-testid="spot-preview-card-wind-unit"
                    >
                      {units.windSpeed.toLowerCase()} {windCardinalDirection}
                    </Typography>
                  </Typography>
                  {showWindObservedBadge && (
                    <Box
                      className={styles.observedBadge}
                      data-testid="spot-preview-card-wind-observed-badge"
                    >
                      <ObservedBadge />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
        {layout === 'THUMBNAIL' && (
          <>
            <Box className={styles.thumbImage}>
              {activeCameras.length > 0 ? (
                <CamStill imageUrl={activeCameras[0].stillUrlFull} />
              ) : (
                <SpotSatelliteWithPin spotName={spotReport.name} geo={geo} size="small" />
              )}
            </Box>
            <Box className={styles.content}>
              <Box className={styles.spotMeta}>
                <Typography
                  variant="subHeadline"
                  component="p"
                  className={styles.spotName}
                  data-testid="spot-preview-card-spot-name"
                >
                  {spotReport.name}
                </Typography>
                {(showSingleCamIcon || showMultiCamIcon || showCameraInsightsIcon) && (
                  <Box className={styles.camIcon}>
                    {showSingleCamIcon && <CamIcon />}
                    {showMultiCamIcon && <CamIcon isMultiCam />}
                    {showCameraInsightsIcon && <CameraInsightsIcon />}
                  </Box>
                )}
              </Box>
              <Box className={styles.spotMeta}>
                {spotReport.conditions.value && (
                  <Box>
                    <CurrentWaveRating direction="VERTICAL" rating={spotReport.conditions.value} />
                  </Box>
                )}
                <Box className={styles.conditions}>
                  <Box className={styles.surfHeight} data-testid="spot-preview-card-surf-height">
                    <Typography component="p" className={styles.surfHeightValue}>
                      <Typography
                        variant="title3"
                        component="span"
                        data-testid="spot-preview-card-surf-height-value"
                      >
                        {spotReport.waveHeight.min}-{spotReport.waveHeight.max}
                      </Typography>
                      <Typography
                        variant="callout1"
                        component="span"
                        data-testid="spot-preview-card-surf-height-unit"
                      >
                        {formattedWaveHeight}
                      </Typography>
                    </Typography>
                    {showSurfObservedBadge && (
                      <Box
                        className={styles.observedBadge}
                        data-testid="spot-preview-card-surf-observed-badge"
                      >
                        <ObservedBadge />
                      </Box>
                    )}
                  </Box>
                  <Box className={styles.wind} data-testid="spot-preview-card-wind">
                    <Typography component="p" className={styles.windValue}>
                      <Typography
                        className={styles.windValue}
                        variant="caption1"
                        component="span"
                        data-testid="spot-preview-card-wind-value"
                      >
                        {spotReport.wind.speed}
                      </Typography>
                      <Typography
                        className={styles.windDirection}
                        variant="caption1"
                        component="span"
                        data-testid="spot-preview-card-wind-unit"
                      >
                        {units.windSpeed.toLowerCase()} {windCardinalDirection},&nbsp;
                      </Typography>
                      <Typography
                        className={styles.windDirectionType}
                        variant="caption1"
                        component="span"
                        data-testid="spot-preview-card-wind-direction-type"
                      >
                        {spotReport.wind.directionType}
                      </Typography>
                    </Typography>
                    {showWindObservedBadge && (
                      <Box
                        className={styles.observedBadge}
                        data-testid="spot-preview-card-wind-observed-badge"
                      >
                        <ObservedBadge />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        )}
        {layout === 'FULL' && (
          <Box className={styles.content}>
            <Box className={styles.spotMeta}>
              <Typography
                variant="subHeadline"
                component="p"
                className={styles.spotName}
                data-testid="spot-preview-card-spot-name"
              >
                {spotReport.name}
              </Typography>
              {showSingleCamIcon && <CamIcon />}
              {showMultiCamIcon && <CamIcon isMultiCam />}
              {showCameraInsightsIcon && <CameraInsightsIcon />}
            </Box>
            {spotReport.conditions.value ? (
              <Box className={styles.currentWaveRating}>
                <CurrentWaveRating rating={spotReport.conditions.value} />
              </Box>
            ) : (
              <Box className={styles.noRating} data-testid="spot-preview-card-no-rating" />
            )}
            <Box className={styles.conditions}>
              <ConditionObservation
                clipPathIds={{ barGraphIcon: clipPathIds.surfBarGraphIcon }}
                iconUrl={observedSurf?.iconUrl}
                observedAt={observedSurf?.at}
                observedBy={observedSurf?.by as ConditionObservers}
                observationValue={observedSurfValue}
                timezone={defaultTimezone}
                unit={isFlat ? '' : formattedWaveHeight}
                isPlus={!!spotReport?.waveHeight?.plus}
              />
              <ConditionObservation
                clipPathIds={{ barGraphIcon: clipPathIds.windBarGraphIcon }}
                observedAt={observedWind.at}
                observedBy={observedWind.by}
                observationValue={observedWind.value}
                observationValueSuffix={mapDegreesCardinal(spotReport?.wind?.direction)}
                timezone={defaultTimezone}
                unit={units?.windSpeed}
              />
            </Box>
          </Box>
        )}
      </>
    ),
    [
      layout,
      spotReport.conditions.value,
      spotReport.name,
      spotReport.waveHeight.min,
      spotReport.waveHeight.max,
      spotReport.waveHeight?.plus,
      spotReport.wind.speed,
      spotReport.wind.directionType,
      spotReport.wind?.direction,
      showSingleCamIcon,
      showMultiCamIcon,
      showCameraInsightsIcon,
      formattedWaveHeight,
      showSurfObservedBadge,
      units.windSpeed,
      windCardinalDirection,
      showWindObservedBadge,
      activeCameras,
      geo,
      clipPathIds,
      observedSurf?.iconUrl,
      observedSurf?.at,
      observedSurf?.by,
      observedSurfValue,
      defaultTimezone,
      isFlat,
      observedWind.at,
      observedWind.by,
      observedWind.value,
    ],
  );

  return (
    <Box
      className={classNames({
        'sl-spot-preview-card-with-clarity-container': true,
        [styles.card]: true,
        [styles[layout.toLowerCase()]]: true,
        [styles.allObserved]: showSurfObservedBadge && showWindObservedBadge,
      })}
      onClick={onClickHandler}
      data-testid={`spot-preview-card-with-clarity-${cardIndex}`}
    >
      {href ? (
        <>
          {camPlayerComponent}
          <WavetrakLink className={styles.container} href={href}>
            {cardContent}
          </WavetrakLink>
        </>
      ) : (
        <Box className={styles.container}>
          {camPlayerComponent && (
            <Box data-testid="spot-preview-card-with-clarity-cam-player-component">
              {camPlayerComponent}
            </Box>
          )}
          {cardContent}
        </Box>
      )}
    </Box>
  );
};

export default SpotPreviewCardWithClarity;
